import { drawer, type DrawerMsg, type DrawerState } from '@llui/components/drawer';
import {
  button,
  div,
  span,
  type Mountable,
  type Renderable,
  type Send,
  type Signal,
} from '@llui/dom';

/** Localised labels for the mobile sheet's visible dismiss affordance. */
export interface BottomSheetLabels {
  close: string;
  dragHint: string;
}

export interface BottomSheetOptions {
  open: Signal<boolean>;
  onDismiss: () => void;
  labelId?: string;
  panelClass?: string;
  testid?: string;
  labels: BottomSheetLabels;
  children: Renderable;
}

let sheetSeq = 0;

/**
 * A thin bottom-side composition of LLui's standard drawer.
 *
 * LLui owns the portal, docking semantics, focus trap/restoration,
 * Escape/outside dismissal, scroll lock, sibling hiding, and dialog ARIA.
 * DungeonLogs adds only the visible mobile handle and safe-area padding.
 */
export function bottomSheet(opts: BottomSheetOptions): Mountable {
  const id = `dl-bottom-sheet-${sheetSeq++}`;
  const panelClass = `dl-bottom-sheet-panel${opts.panelClass ? ` ${opts.panelClass}` : ''}`;
  const state = opts.open.map(
    (open): DrawerState => ({
      open,
      status: open ? 'open' : 'closed',
      skipAnimations: true,
    }),
  );
  const send: Send<DrawerMsg> = (msg) => {
    if (
      msg.type === 'close' ||
      msg.type === 'toggle' ||
      (msg.type === 'setOpen' && !msg.open)
    ) {
      opts.onDismiss();
    }
  };
  const parts = drawer.connect(state, send, {
    id,
    side: 'bottom',
    closeLabel: opts.labels.close,
  });

  return div({ class: 'dl-bottom-sheet-boundary' }, [
    drawer.overlay({
      state,
      send,
      parts,
      positionerClass: 'dl-bottom-sheet',
      content: () => [
        div(
          {
            ...parts.content,
            class: panelClass,
            'aria-labelledby': opts.labelId,
            'data-testid': opts.testid,
          },
          [
            button(
              {
                ...parts.closeTrigger,
                class: 'dl-bottom-sheet-handle',
                'data-testid': opts.testid
                  ? `${opts.testid}-handle`
                  : 'dl-bottom-sheet-handle',
              },
              [
                span({ class: 'dl-bottom-sheet-handle-bar', 'aria-hidden': 'true' }),
                span({ class: 'dl-cal-vh' }, [opts.labels.dragHint]),
              ],
            ),
            div({ class: 'dl-bottom-sheet-content' }, opts.children),
          ],
        ),
      ],
    }),
  ]);
}
